import { OnetrustConsentProvider } from './onetrust-consent.provider'
import { createOptanonWrapper } from './optanon-wrapper/optanon-wrapper'
import { setOneTrustVariantCookie } from './model/set-one-trust-variant-cookie'
import {
  ConsentProviderConfig,
  ConsentProviderPort,
  LoadConsentProviderPort,
} from '../ports'
import {
  EnvironmentConfig,
  LoggingPort,
  RetryStrategy,
} from '@otto-ec/eprivacy-shared'
import { getOnetrustSdkSrc } from './model/get-onetrust-sdk-src'
import { loadOnetrustSdk } from './load-one-trust-sdk/load-onetrust-sdk'
import { OneTrust } from './model/onetrust'

export const loadOneTrustConsentProviderFactory = (
  logger: LoggingPort,
  envConfig: EnvironmentConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy
): LoadConsentProviderPort => {
  return async (config: ConsentProviderConfig, onetrustVariantId?: string) => {
    return await loadOneTrustProvider(
      onetrustVariantId,
      envConfig,
      config,
      isOneTrustInternalHostingEnabled,
      retryWrapper,
      logger
    )
  }
}

async function configureAndLoadOneTrustSdk(
  onetrustVariantId: string | undefined,
  envConfig: EnvironmentConfig,
  config: ConsentProviderConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy,
  logger: LoggingPort
) {
  const oneTrustPromise = new Promise<OneTrust>((resolve, reject) =>
    createOptanonWrapper(resolve, reject)
  )

  if (onetrustVariantId) {
    setOneTrustVariantCookie(onetrustVariantId, envConfig.stage)
  }

  const oneTrustSrc = getOnetrustSdkSrc(
    config.onetrust.onetrustSiteConfig,
    envConfig.stage,
    isOneTrustInternalHostingEnabled
  )

  const checkScriptIntegrity = !isOneTrustInternalHostingEnabled

  let failedAttempts = 0

  await retryWrapper.run<void>(
    () => loadOnetrustSdk(config, oneTrustSrc, checkScriptIntegrity),
    () => failedAttempts++
  )
  if (failedAttempts > 0) {
    logger.info(
      `Successfully loaded OneTust-SDK after ${failedAttempts} attempts.`
    )
  }
  return oneTrustPromise
}

async function loadOneTrustProvider(
  onetrustVariantId: string | undefined,
  envConfig: EnvironmentConfig,
  config: ConsentProviderConfig,
  isOneTrustInternalHostingEnabled: boolean,
  retryWrapper: RetryStrategy,
  logger: LoggingPort
): Promise<ConsentProviderPort> {
  const oneTrust = await configureAndLoadOneTrustSdk(
    onetrustVariantId,
    envConfig,
    config,
    isOneTrustInternalHostingEnabled,
    retryWrapper,
    logger
  )
  return new OnetrustConsentProvider(oneTrust)
}
